// Phase 10 — Subscriptions route. Read-only list view with billing info.

import * as React from 'react';
import { copy } from '@dailydeck/shared/copy';
import { useSubscriptions } from '../../hooks/useSubscriptions.js';
import { Card, CardContent } from '../../components/ui/Card.js';
import { SkeletonRow } from '../../components/ui/Skeleton.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { Button } from '../../components/ui/Button.js';

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function cycleLabel(cycle: string): string {
  const labels: Record<string, string> = {
    weekly: copy.subscriptions.cycleWeekly,
    monthly: copy.subscriptions.cycleMonthly,
    yearly: copy.subscriptions.cycleYearly,
  };
  return labels[cycle] ?? cycle;
}

export function Subscriptions(): JSX.Element {
  React.useEffect(() => {
    document.title = `DailyDeck — ${copy.subscriptions.pageTitle}`;
  }, []);

  const query = useSubscriptions();

  return (
    <div className="space-y-6" data-testid="subscriptions-route">
      <header>
        <h1 className="text-title-1 font-semibold text-text-primary">
          {copy.subscriptions.pageTitle}
        </h1>
        <p className="text-body text-text-secondary mt-1">
          {copy.subscriptions.pageSubtitle}
        </p>
      </header>

      {query.isPending && (
        <div className="rounded-lg border border-border-subtle bg-surface-raised" aria-label={copy.subscriptions.loadingLabel}>
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {query.isError && (
        <Card>
          <CardContent className="py-4">
            <p className="text-body-strong text-text-primary">{copy.subscriptions.errorTitle}</p>
            <p className="text-caption text-text-secondary mt-1">{copy.errors.network_unavailable}</p>
            <Button variant="secondary" className="mt-3" onClick={() => void query.refetch()}>
              {copy.subscriptions.retry}
            </Button>
          </CardContent>
        </Card>
      )}

      {query.isSuccess && query.data.length === 0 && (
        <EmptyState title={copy.subscriptions.emptyTitle} description={copy.subscriptions.emptyBody} />
      )}

      {query.isSuccess && query.data.length > 0 && (
        <div className="space-y-2">
          {query.data.map((sub) => (
            <Card key={sub.id}>
              <CardContent className="flex items-start justify-between gap-4 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-body-strong font-medium text-text-primary">
                      {sub.name}
                    </h3>
                    {!sub.active && (
                      <span className="rounded-full bg-surface-raised border border-border-default px-2 py-0.5 text-micro text-text-muted">
                        Inactive
                      </span>
                    )}
                  </div>
                  {sub.provider && (
                    <p className="text-caption text-text-secondary">{sub.provider}</p>
                  )}
                  {sub.notes && (
                    <p className="text-caption text-text-muted mt-1 line-clamp-2">{sub.notes}</p>
                  )}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span className="text-body-strong font-semibold text-text-primary tabular-nums">
                    {formatCents(sub.amount_cents)}
                  </span>
                  <span className="text-caption text-text-muted">{cycleLabel(sub.billing_cycle)}</span>
                  {sub.next_billing_date && (
                    <span className="text-micro text-text-muted tabular-nums">
                      Next: {sub.next_billing_date}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
